import React from 'react';
import { FadeIn, StaggerContainer, StaggerItem } from './FadeIn';
import { ShieldCheckIcon, CodeIcon, NetworkIcon, LandmarkIcon } from 'lucide-react';
interface Founder {
  name: string;
  title: string;
  image?: string;
  initials: string;
  bio: string[];
  highlights: {
    icon: React.ElementType;
    label: string;
  }[];
}
const founders: Founder[] = [{
  name: 'Arthur Miller',
  title: 'Founder, CEO, CTO & CISO',
  image: 'https://cdn.magicpatterns.com/uploads/caDcm14Pxfktf7goeivfVz/image3.png',
  initials: 'AM',
  bio: ['Arthur Miller is a technology executive and enterprise architect with deep experience building secure, large-scale financial infrastructure. His career has focused on modernizing financial systems, designing high-availability distributed platforms, and implementing security architectures for regulated industries.', 'Arthur previously served as a lead engineer working on applications supporting the U.S. Defense Information Systems Agency (DISA) contracted through Tasc (Enginity) and later became an enterprise architect and engineering leader within the financial services industry. He played a key architectural role in the development and scaling of Zelle® and Real-Time Payments (RTP) infrastructure, helping financial institutions adopt modern real-time payment systems at a national scale.', "He later served as Chief Technology Officer of a white-label fintech company focused on Buy-Now-Pay-Later lending solutions, where he led the development of lending infrastructure designed to integrate directly into financial institutions' digital banking ecosystems.", 'Arthur has spent much of his career advising financial institutions and fintech organizations on enterprise architecture, secure integration design, cloud infrastructure, distributed systems, and regulatory-grade security frameworks. His experience across defense systems, national payment networks, and fintech lending platforms uniquely positions him to lead FiQIS in building the next generation of secure financial infrastructure.', 'At FiQIS, Arthur leads company strategy, platform architecture, security design, and regulatory alignment as the organization builds infrastructure that enables financial institutions to modernize safely while maintaining ownership of the customer relationship.'],
  highlights: [{
    icon: ShieldCheckIcon,
    label: 'Ex-DISA & Defense Systems'
  }, {
    icon: LandmarkIcon,
    label: 'Ex-Zelle® & RTP Infrastructure'
  }, {
    icon: NetworkIcon,
    label: 'Expert Enterprise Architect'
  }]
}, {
  name: 'Mark Youwanes',
  title: 'Co-Founder & Vice President of Engineering',
  image: 'https://cdn.magicpatterns.com/uploads/fhjUHjqZ4nivpWBMza7DH6/Screenshot_2026-03-09_at_5.20.57_PM.png',
  initials: 'MY',
  bio: ['Mark Youwanes is an experienced software engineering leader specializing in building scalable platforms and managing high-performance engineering teams. His background spans full-stack development, distributed systems, cloud infrastructure, and modern application architecture.', 'Throughout his career, Mark has led engineering teams responsible for designing and deploying complex enterprise platforms that operate at scale. He has extensive experience implementing secure APIs, cloud-native architectures, and resilient microservice ecosystems, enabling organizations to deliver reliable financial technology solutions.', "At FiQIS, Mark oversees platform development and engineering execution. He is responsible for building the company's core technology stack, managing the engineering organization, and ensuring the FiQIS platform meets the performance, scalability, and security requirements expected by regulated financial institutions."],
  highlights: [{
    icon: CodeIcon,
    label: 'Full-Stack & Distributed Systems'
  }, {
    icon: NetworkIcon,
    label: 'Cloud-Native Architecture'
  }, {
    icon: ShieldCheckIcon,
    label: 'Secure API Design'
  }]
}];
export function AboutSection() {
  return <section id="about" className="py-24 bg-navy relative overflow-hidden" data-id="element-13">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-teal/3 rounded-full blur-[150px] pointer-events-none" data-id="element-14"></div>

      <div className="max-w-7xl mx-auto px-6" data-id="element-15">
        <FadeIn className="mb-20 max-w-3xl" data-id="element-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium mb-6" data-id="element-17">
            Leadership
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-18">
            Built by Engineers Who've Scaled{' '}
            <span className="text-teal" data-id="element-19">National Financial Infrastructure</span>
          </h2>
          <p className="text-xl text-gray-400" data-id="element-20">
            Our founding team brings decades of experience from defense systems,
            national payment networks, and enterprise fintech platforms.
          </p>
        </FadeIn>

        <StaggerContainer className="space-y-16" data-id="element-21">
          {founders.map((founder, index) => <StaggerItem key={founder.name} data-id="element-22">
              <div className="bg-slate/60 border border-white/5 rounded-2xl overflow-hidden" data-id="element-23">
                <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`} data-id="element-24">
                  {/* Photo / Avatar Column */}
                  <div className="lg:w-[340px] flex-shrink-0 relative" data-id="element-25">
                    {founder.image ? <div className="relative h-80 lg:h-full min-h-[400px]" data-id="element-26">
                        <img src={founder.image} alt={founder.name} className="w-full h-full object-cover object-top" data-id="element-27" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate/90 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-slate/60" data-id="element-28"></div>
                      </div> : <div className="h-80 lg:h-full min-h-[400px] bg-gradient-to-br from-slate to-navy flex items-center justify-center relative" data-id="element-29">
                        <div className="w-40 h-40 rounded-full bg-teal/10 border-2 border-teal/30 flex items-center justify-center" data-id="element-30">
                          <span className="text-5xl font-bold text-teal" data-id="element-31">
                            {founder.initials}
                          </span>
                        </div>
                        {/* Decorative elements */}
                        <div className="absolute top-12 right-12 w-20 h-20 border border-white/5 rounded-full" data-id="element-32"></div>
                        <div className="absolute bottom-16 left-10 w-12 h-12 border border-teal/10 rounded-full" data-id="element-33"></div>
                      </div>}
                  </div>

                  {/* Content Column */}
                  <div className="flex-1 p-8 lg:p-10" data-id="element-34">
                    <div className="mb-6" data-id="element-35">
                      <h3 className="text-3xl font-bold text-white mb-2" data-id="element-36">
                        {founder.name}
                      </h3>
                      <p className="text-teal font-semibold text-lg" data-id="element-37">
                        {founder.title}
                      </p>
                    </div>

                    {/* Highlight Tags */}
                    <div className="flex flex-wrap gap-3 mb-8" data-id="element-38">
                      {founder.highlights.map(h => {
                    const Icon = h.icon;
                    return <div key={h.label} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-navy border border-white/10 text-sm text-gray-300" data-id="element-39">
                            <Icon className="w-4 h-4 text-teal" data-id="element-40" />
                            {h.label}
                          </div>;
                  })}
                    </div>

                    {/* Bio */}
                    <div className="space-y-4" data-id="element-41">
                      {founder.bio.map((paragraph, i) => <p key={i} className="text-gray-400 leading-relaxed text-[15px]" data-id="element-42">
                          {paragraph}
                        </p>)}
                    </div>
                  </div>
                </div>
              </div>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </section>;
}